var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { customElement, property } from 'lit/decorators.js';
import { html, nothing } from 'lit';
import { BwElement } from './base/bw-element.js';
let BwButton = class BwButton extends BwElement {
    constructor() {
        super(...arguments);
        this.kind = 'default';
        this.tone = 'default';
        this.size = 'md';
        this.shape = 'rounded';
        this.type = 'button';
        // `pressed` is special — null means absent (action mode),
        // boolean true/false means toggle mode (aria-pressed reflects).
        this.pressed = null;
        this.disabled = false;
        this.loading = false;
        // `block` — stretch to fill the container width (review #1). Default is
        // fit-content (inline-flex); block makes the host + cap span 100%.
        this.block = false;
        // `selected` — pure boolean pressed-in affordance for toggles whose on/off
        // state is owned by app state (not the button). Unlike `pressed` it does NOT
        // auto-flip on click or fire bw-toggle; it only paints the recessed look and
        // exposes aria-pressed. B-260626.
        this.selected = false;
        // Accessible name — callers set `aria-label` on the HOST (every existing
        // consumer does this: bw-transport.ts, bw-track-cell.ts, chart-buttons.ts,
        // desktop's icon-only close/edit buttons…). But the real `<button>` a
        // screen reader focuses is `.bw-button-inner`, and the host itself carries
        // no ARIA role, so it reads as a generic element — the name never reached
        // AT. Redeclaring `ariaLabel` (normally inherited from ARIAMixin on every
        // HTMLElement) as a Lit reactive property lets us observe the attribute and
        // forward its value into the template below. `reflect: true` keeps writing
        // it back to the host attribute too, so existing `bw-button[aria-label]`
        // queries (tests + CSS) keep working untouched.
        this.ariaLabel = null;
        // Menu / disclosure / description state — same story as the name above.
        // Callers set `aria-expanded`, `aria-haspopup` (a "⋯" that opens a menu),
        // `aria-controls` (a disclosure toggle) and `aria-describedby` (why a
        // disabled button is disabled) on the HOST, but the host is a generic
        // element, so AT never saw them; the real <button> is the inner one.
        // Observed from the host attribute and forwarded, removal removes. No
        // `reflect`: the host attribute is the source of truth and stays as the
        // caller wrote it. Deliberately NOT the ARIAMixin accessors (`ariaExpanded`,
        // `ariaHasPopup`) — redeclaring those replaces the native reflecting setter,
        // and `ariaControls` / `ariaDescribedBy` have no string accessor at all — so
        // `el.ariaExpanded = …` keeps writing the host attribute natively and flows
        // in through here. `aria-describedby` is an id list (space-separated) and is
        // forwarded verbatim; the ids resolve in the document, which is the same one
        // for host and inner button in light DOM.
        this.fwdExpanded = null;
        this.fwdHasPopup = null;
        this.fwdControls = null;
        this.fwdDescribedBy = null;
        this._onClick = (e) => {
            if (this.disabled || this.loading) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            const isToggle = this.pressed !== null;
            if (isToggle) {
                this.pressed = !this.pressed;
                this.dispatchEvent(new CustomEvent('bw-toggle', {
                    detail: { pressed: this.pressed },
                    bubbles: true,
                    composed: true,
                }));
            }
            this.dispatchEvent(new CustomEvent('bw-click', {
                detail: { originalEvent: e },
                bubbles: true,
                composed: true,
            }));
        };
    }
    render() {
        const isToggle = this.pressed !== null;
        // aria-pressed: toggle mode reflects `pressed`; otherwise `selected` (if on)
        // surfaces the boolean toggle state to AT even in action mode.
        const ariaPressed = isToggle
            ? String(Boolean(this.pressed))
            : (this.selected ? 'true' : null);
        const ariaBusy = this.loading ? 'true' : null;
        return html `
      <button
        class="bw-button-inner"
        type=${this.type}
        ?disabled=${this.disabled || this.loading}
        aria-label=${this.ariaLabel ?? nothing}
        aria-pressed=${ariaPressed ?? nothing}
        aria-busy=${ariaBusy ?? nothing}
        aria-expanded=${this.fwdExpanded ?? nothing}
        aria-haspopup=${this.fwdHasPopup ?? nothing}
        aria-controls=${this.fwdControls ?? nothing}
        aria-describedby=${this.fwdDescribedBy ?? nothing}
        @click=${this._onClick}
      >
        <span class="bw-button-icon-start"><slot name="icon-start"></slot></span>
        <span class="bw-button-label"><slot></slot></span>
        <span class="bw-button-icon-end"><slot name="icon-end"></slot></span>
        <span class="bw-button-spinner" ?hidden=${!this.loading}></span>
      </button>
    `;
    }
    firstUpdated() {
        // Light DOM has no native slot projection — move author children into
        // the rendered template's slot positions. See B-054 / BwElement.
        this.projectSlots();
    }
};
__decorate([
    property({ type: String, reflect: true })
], BwButton.prototype, "kind", void 0);
__decorate([
    property({ type: String, reflect: true })
], BwButton.prototype, "tone", void 0);
__decorate([
    property({ type: String, reflect: true })
], BwButton.prototype, "size", void 0);
__decorate([
    property({ type: String, reflect: true })
], BwButton.prototype, "shape", void 0);
__decorate([
    property({ type: String, reflect: true })
], BwButton.prototype, "type", void 0);
__decorate([
    property({
        converter: {
            fromAttribute: (v) => v === null ? null : v !== 'false',
            toAttribute: (v) => v === null ? null : String(v),
        },
        reflect: true,
    })
], BwButton.prototype, "pressed", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwButton.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwButton.prototype, "loading", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwButton.prototype, "block", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwButton.prototype, "selected", void 0);
__decorate([
    property({ type: String, attribute: 'aria-label', reflect: true })
], BwButton.prototype, "ariaLabel", void 0);
__decorate([
    property({ type: String, attribute: 'aria-expanded' })
], BwButton.prototype, "fwdExpanded", void 0);
__decorate([
    property({ type: String, attribute: 'aria-haspopup' })
], BwButton.prototype, "fwdHasPopup", void 0);
__decorate([
    property({ type: String, attribute: 'aria-controls' })
], BwButton.prototype, "fwdControls", void 0);
__decorate([
    property({ type: String, attribute: 'aria-describedby' })
], BwButton.prototype, "fwdDescribedBy", void 0);
BwButton = __decorate([
    customElement('bw-button')
], BwButton);
export { BwButton };
//# sourceMappingURL=bw-button.js.map